import {
  ActionField,
  AsyncField,
  Field,
  useFormContext,
  withForm,
} from '@oscar/form';
import {
  Button,
  Col,
  Input,
  Modal,
  notification,
  Radio,
  Row,
  Select,
} from '@oscar/theme';
import { APIS } from 'common/constants/endpoints';
import { debounce, isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';

const { Option } = Select;

const optionsUser = [
  { label: 'PETRONAS', value: 0 },
  { label: 'Other', value: 1 },
];

export type AddEditPopupProps = {
  title: string;
  isVisible: boolean;
  handleCancel: () => void;
  labelConfirm?: string;
  typePopup: 'add' | 'edit';
  record?: any;
  actionTable?: any;
};

const AddEditPopup: React.FC<AddEditPopupProps> = ({
  title,
  isVisible,
  handleCancel,
  labelConfirm,
  typePopup,
  record,
  actionTable,
}) => {
  const [formContext] = useFormContext();
  const [type, setType] = useState(0);
  const [userExists, setUserExists] = useState(false);
  const [name, setName] = useState('');

  const handleOnCancel = () => {
    handleCancel();
    formContext.form?.reset?.();
    if (typePopup === 'add') {
      setType(0);
    }
  };

  useEffect(() => {
    if (typePopup === 'edit') {
      formContext?.form?.setFieldsValue({
        userId: record?.userId,
        type: record?.type === '0' ? 0 : 1,
        servicesProviderId: record?.servicesProviderId,
        name: record?.name,
        email: record?.email,
        roles: record?.listRoleId,
      });
      setType(record?.type === '0' ? 0 : 1);
    }
  });
  useEffect(() => {
    if (typePopup === 'add') {
      if (type === 0) {
        formContext?.form?.setFieldsValue({
          name: null,
          email: null,
          roles: [],
        });
      } else {
        formContext?.form?.setFieldsValue({
          name: null,
          email: null,
          roles: [4],
        });
      }
      setName('');
    }
  }, [type, typePopup]);

  const handleNameChange = (value: any, options: any) => {
    const selectedItem = options.find(
      (option: any) => option?.displayName === value,
    );
    formContext?.form?.setFieldsValue({
      name: value,
      email: selectedItem?.email,
    });
    setName(value);
    setUserExists(false);
  };

  const validateForm = () => {
    const data = formContext.form?.getFieldsValue();
    if (type === 0) {
      return (
        isEmpty(data?.name) || isEmpty(data?.email) || isEmpty(data?.roles)
      );
    } else if (type === 1) {
      return (
        isEmpty(data?.name) ||
        isEmpty(data?.email) ||
        isEmpty(data?.roles) ||
        isEmpty(data?.servicesProviderId)
      );
    }
  };

  const showMessage = (code: number) => {
    if (code === 1) {
      notification['success']({
        message: 'Successfully!',
        description: `User has been ${
          typePopup === 'add' ? 'added' : 'edited'
        }`,
      });
      setUserExists(false);
    }
    if (code === 2) {
      setUserExists(true);
    }
  };

  return (
    <ActionField
      name={'userManagement'}
      request={{
        name: 'Save',
        url: typePopup === 'add' ? APIS.CREATE_USER : APIS.EDIT_USER,
        method: 'POST',
        transformRequest: (data: any) => {
          return {
            ...data,
            userId: record?.userId,
            type,
          };
        },
      }}
      onResponseData={(res: any) => {
        showMessage(res?.code);
        if (res?.code === 1) {
          actionTable?.fetchData?.({});
          actionTable?.methods?.fetchData?.({});
          handleOnCancel();
        }
      }}
    >
      {(actionProps: any) => {
        return (
          <Modal
            destroyOnClose={true}
            afterClose={() => {
              formContext.form?.reset?.();
              if (typePopup === 'add') {
                setType(0);
              }
            }}
            className="popup-custom"
            title={title}
            maskTransitionName=""
            visible={isVisible}
            onCancel={handleOnCancel}
            footer={[
              <Button size="middle" key="back" onClick={handleOnCancel}>
                Cancel
              </Button>,
              <Button
                className="btn-submit"
                size="middle"
                key="submit"
                onClick={() => {
                  actionProps.submitData();
                }}
                disabled={validateForm()}
              >
                {labelConfirm}
              </Button>,
            ]}
          >
            {typePopup === 'add' && (
              <Row className="user-popup-row-option" gutter={12}>
                <Col span={24}>
                  <Field name={'type'} initialValue={0}>
                    <Radio.Group
                      options={optionsUser}
                      onChange={(e: any) => setType(e.target.value)}
                    />
                  </Field>
                </Col>
              </Row>
            )}
            {typePopup === 'edit' && (
              <Row className="user-popup-row-option" gutter={12}>
                <Col span={24}>
                  <div className="radio-type-user">
                    <Radio checked={type === 0} disabled={type !== 0}>
                      PETRONAS
                    </Radio>
                    <Radio checked={type === 1} disabled={type !== 1}>
                      Other
                    </Radio>
                  </div>
                </Col>
              </Row>
            )}
            {type === 1 && (
              <AsyncField
                name={'fetchServiceProvider'}
                requests={[
                  {
                    name: 'serviceProvider',
                    url: APIS.META_DATA_USER_COMPANY,
                    method: 'GET',
                  },
                ]}
              >
                {(serviceProviderProps: any) => {
                  const options =
                    serviceProviderProps?.data?.serviceProvider?.company || [];

                  return (
                    <Row className="user-popup-row" gutter={12}>
                      <Col span={24}>
                        <p className="label-normal">Service Provider</p>
                        <Field name={'servicesProviderId'}>
                          <Select
                            showSearch
                            className="user-popup-select"
                            allowClear
                            filterOption={false}
                          >
                            {options.map?.((item: any, index: any) => {
                              return (
                                <Option
                                  value={item?.id}
                                  key={`${item?.id}${index}`}
                                >
                                  {item?.label}
                                </Option>
                              );
                            })}
                          </Select>
                        </Field>
                      </Col>
                    </Row>
                  );
                }}
              </AsyncField>
            )}

            {typePopup === 'add' ? (
              <AsyncField
                name={'fetchUserLeaderName'}
                requests={[
                  {
                    name: 'userLeaderName',
                    url: APIS.AZURE_AD_USER,
                    method: 'POST',
                    nextParams: (nextProps: any) => {
                      return {
                        limit: 6,
                        name: nextProps?.customData?.textSearch || '',
                        type,
                      };
                    },
                  },
                ]}
              >
                {(userLeaderNameProps: any) => {
                  const options =
                    userLeaderNameProps?.data?.userLeaderName || [];

                  return (
                    <>
                      <Row className="user-popup-row" gutter={12}>
                        <Col span={24}>
                          <p className="label-normal">Name</p>
                          <Field name={'name'}>
                            <Select
                              value={name}
                              showSearch
                              className="user-popup-select"
                              allowClear
                              filterOption={false}
                              onChange={(value: any) =>
                                handleNameChange(value, options)
                              }
                              onSearch={debounce((value: any) => {
                                userLeaderNameProps.reloadData({
                                  textSearch: value,
                                });
                              }, 300)}
                            >
                              {options.map?.((item: any, index: any) => {
                                return (
                                  <Option
                                    value={item?.displayName}
                                    key={`${item?.id}${index}`}
                                  >
                                    {item?.displayName}
                                  </Option>
                                );
                              })}
                            </Select>
                            {userExists && (
                              <div className="message-user-add-error">
                                User already exists
                              </div>
                            )}
                          </Field>
                        </Col>
                      </Row>
                      <Row className="user-popup-row" gutter={12}>
                        <Col span={24}>
                          <p className="label-normal">Email</p>
                          <Field name={'email'}>
                            <Input readOnly={true} />
                          </Field>
                        </Col>
                      </Row>
                    </>
                  );
                }}
              </AsyncField>
            ) : (
              <>
                <Row className="user-popup-row" gutter={12}>
                  <Col span={24}>
                    <p className="label-normal">Name</p>
                    <Field name={'name'}>
                      <Input disabled={typePopup === 'edit'} />
                    </Field>
                  </Col>
                </Row>
                <Row className="user-popup-row" gutter={12}>
                  <Col span={24}>
                    <p className="label-normal">Email</p>
                    <Field name={'email'}>
                      <Input disabled={typePopup === 'edit'} readOnly={true} />
                    </Field>
                  </Col>
                </Row>
              </>
            )}
            <Row className="user-popup-row" gutter={12}>
              <Col span={24}>
                <p className="label-normal">Role</p>
                <Field name={'roles'}>
                  <Select
                    mode="multiple"
                    size="large"
                    className="user-popup-select"
                    allowClear
                    disabled={type === 1}
                  >
                    <Option value={1}>Administrator</Option>
                    <Option value={2}>Requestor</Option>
                    <Option value={3}>Insulation Team</Option>
                    <Option value={5}>Painting Team</Option>
                    {type === 1 && <Option value={4}>Contractor</Option>}
                  </Select>
                </Field>
              </Col>
            </Row>
          </Modal>
        );
      }}
    </ActionField>
  );
};

export default withForm<AddEditPopupProps>({
  name: 'apiAddEditPopup',
  preload: [],
})(AddEditPopup);
